import { toLocaleString } from "@/utils/number";
import { pluralize } from "@/utils/string";
import { Separator } from "../ui/separator";
import { SimpleTooltip } from "../Common/Tooltip/SimpleTooltip";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { useMemo } from "react";
import { MINIMUM_STRIPE_TX_AMOUNT, MIN_STRIPE_TX_AMT_AFTER_PROVIDER_FEE } from "@/constants";

type SummaryPriceType = {
  total?: number;
  average?: number;
  hardwareQuantity?: number;
  hourCount?: number;
  hideFeeSection?: boolean;
  hardwareType?: string;
  currency: "iocoin" | "usdc" | "credits" | "stripe";
  providerFee?: number;
};

export const SummaryPrice = ({
  total,
  average,
  hardwareQuantity,
  hourCount,
  currency,
  hideFeeSection,
  hardwareType = "GPUs/CPUs",
  providerFee
}: SummaryPriceType) => {
  const decimalPoints = currency === "iocoin" ? 3 : 2;

  const calculatedProviderFee = useMemo(() => {
    if (!total || !providerFee) {
      return 0;
    }

    if (total > MINIMUM_STRIPE_TX_AMOUNT) {
      return providerFee;
    }

    return MINIMUM_STRIPE_TX_AMOUNT - MIN_STRIPE_TX_AMT_AFTER_PROVIDER_FEE;
  }, [total, providerFee]);

  return (
    <div className="lg:subtext-small subtext-x-small my-4  w-full text-gray">
      <div className="mb-2 flex justify-between">
        <span>Quantity ({hardwareType})</span>
        <span className="text-right">{hardwareQuantity}</span>
      </div>
      <div className="mb-2 flex justify-between">
        <span className="text-left">Average Per Card</span>
        <span className="text-right">
          {average ? `${formatPrice(currency, average, decimalPoints)}/hr` : "-"}
        </span>
      </div>
      <div className="mb-2 flex justify-between">
        <span className="text-left">Hours</span>
        <span className="text-right">{pluralize("hour", hourCount)}</span>
      </div>
      <div className="mb-2 flex justify-between">
        <span className="text-left">Total Cost Per Card</span>
        <span className="text-right text-foreground">
          {average && hourCount
            ? `${formatPrice(currency, average * hourCount, decimalPoints)}`
            : "-"}
        </span>
      </div>
      <div className="flex justify-between">
        <span className="text-left">Cost</span>
        <span className="text-right text-foreground">
          {formatPrice(currency, total, decimalPoints)}
        </span>
      </div>

      {currency === "stripe" && total && total < MINIMUM_STRIPE_TX_AMOUNT ? (
        <div className="mt-2 flex justify-between">
          <div className="flex items-center gap-2 text-left">
            Round-up amount
            <SimpleTooltip
              id="fiat-payment-roundup"
              content={
                <div className="subtext-small rounded border border-gray-dark-200 bg-gray-dark-600 px-2.5 py-1 text-foreground">
                  This will be credited to your IO Credits balance.
                </div>
              }
              place="top"
              tooltipClassName="!p-0"
            >
              <InfoCircledIcon className="h-4 w-4" />
            </SimpleTooltip>
          </div>
          {/* Min base is $10; all-in with 5% provider fee is $10.50 */}
          <span className="text-right text-foreground">
            {formatPrice(currency, MIN_STRIPE_TX_AMT_AFTER_PROVIDER_FEE - total, 2)}
          </span>
        </div>
      ) : null}
      {!hideFeeSection && currency === "stripe" && (
        <>
          <Separator className="my-4" />
          <div className="flex justify-between">
            <span className="text-left">Payment Provider Fee (5%)</span>
            <span className="text-right text-foreground">
              {formatPrice(currency, calculatedProviderFee, 2)}
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export const formatPrice = (
  currency: "iocoin" | "usdc" | "credits" | "stripe",
  value?: number,
  decimals = 2
) => {
  if (currency === "iocoin") {
    return `$IO ${toLocaleString(value, decimals)}`;
  }
  if (currency === "credits") {
    return `$${toLocaleString(value, decimals)}`;
  }

  if (currency === "stripe") {
    return `${toLocaleString(value, decimals)} USD`;
  }
  return `${toLocaleString(value, decimals)} USDC`;
};
